import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import RevealText from "../components/RevealText";
import MediaImage from "../components/MediaImage";
import MediaVideo from "../components/MediaVideo";
import { getProjectPhotography, getProjectVideoIds, getProjectVideos } from "../lib/data";
import { useProject } from "../hooks/useProjects";

const mediaReveal = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function CaseStudy() {
  const { slug } = useParams();
  const { data: project, isLoading, isFetching } = useProject(slug);

  if (!project && !isLoading && !isFetching) return <Navigate to="/work" replace />;
  if (!project) {
    return (
      <div className="pt-[calc(var(--site-header-offset)+2rem)] md:pt-40 px-6 md:px-12 pb-24" data-testid={`page-case-study-${slug}`}>
        <p className="font-body uppercase text-xs tracking-[0.2em] text-black/40">Loading…</p>
      </div>
    );
  }

  const videos = getProjectVideos(project);
  const videoIds = getProjectVideoIds(project);
  const photos = getProjectPhotography(project);
  const hasVideos = videos.length > 0;
  const hasPhotography = photos.length > 0;
  const showOverview = !project.hideOverview;
  const mediaCardClass = "w-full aspect-[4/5]";

  return (
    <div data-testid={`page-case-study-${slug}`} className="pb-16 md:pb-24 lg:pb-32">
      {/* HERO */}
      <section className="pt-[calc(var(--site-header-offset)+1.75rem)] md:pt-32 px-6 md:px-12 pb-4 md:pb-6">
        <Link to="/work" className="inline-flex items-center gap-2 font-body uppercase text-xs tracking-[0.2em] mb-6 md:mb-8 link-line" data-testid="case-back-link"><ArrowLeft className="w-3.5 h-3.5" /> Back to work</Link>
        <div className="max-w-5xl">
          <p className="font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-2">{project.industry}</p>
          <h1 className="font-display uppercase text-[clamp(2.25rem,10vw,3.25rem)] md:text-[clamp(2.75rem,5.5vw,4.75rem)] leading-[0.92] tracking-mega">
            <RevealText>{project.title}</RevealText>
          </h1>
          <p className="font-editorial italic text-lg md:text-xl lg:text-2xl text-black/75 mt-4 md:mt-5 lg:mt-6 max-w-2xl leading-snug">
            &ldquo;{project.tagline}&rdquo;
          </p>
        </div>
      </section>

      {/* FILMS */}
      {hasVideos && (
        <section className="px-6 md:px-12 pb-8 md:pb-10" data-testid="case-videos">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {videos.map((video, i) => {
              const isExtraSauce = project.slug === "extra-sauce";
              return (
              <motion.div key={video.publicId || i} {...mediaReveal} className={mediaCardClass}>
                <MediaVideo
                  publicId={video.publicId}
                  title={
                    isExtraSauce && video.brand
                      ? video.brand
                      : i === 0
                        ? project.title
                        : `${project.title} — Cut ${i + 1}`
                  }
                  videoIds={videoIds}
                  aspect="h-full w-full"
                  credit={
                    isExtraSauce && video.brand
                      ? {
                          brand: video.brand,
                          tagline: video.tagline,
                          industry: video.industry || project.industry,
                          color: video.color || project.color,
                        }
                      : null
                  }
                />
              </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* OVERVIEW */}
      {showOverview && (
      <section
        className="px-6 md:px-12 pt-10 pb-10 md:pt-14 md:pb-12 lg:pt-16 lg:pb-14"
        data-testid="case-overview"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-x-16 lg:gap-y-0 items-start">
          <aside className="lg:col-span-3 lg:sticky lg:top-28 self-start">
            <div className="flex flex-col gap-6 lg:gap-8">
              <div className="min-w-0">
                <p className="font-body uppercase text-sm md:text-base tracking-[0.2em] text-black/50 mb-3 md:mb-4">
                  Client
                </p>
                <p className="font-body font-normal text-base md:text-2xl leading-snug text-black">
                  {project.client}
                </p>
              </div>
              <div className="min-w-0 lg:w-full">
                <p className="font-body uppercase text-sm md:text-base tracking-[0.2em] text-black/50 mb-3 md:mb-4">
                  Services
                </p>
                <ul className="font-body font-normal text-base md:text-xl leading-snug text-black/90 space-y-1.5 md:space-y-2">
                  {project.services.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9 lg:pt-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-x-10 lg:gap-x-14 items-start">
              <article>
                <h2 className="font-display text-[clamp(1.75rem,4vw,2.75rem)] uppercase tracking-mega mb-4 md:mb-5">
                  <RevealText>The challenge.</RevealText>
                </h2>
                <p className="font-body text-base md:text-lg lg:text-xl text-black/80 leading-relaxed">
                  {project.challenge}
                </p>
              </article>

              <article>
                <h2 className="font-display text-[clamp(1.75rem,4vw,2.75rem)] uppercase tracking-mega mb-4 md:mb-5">
                  <RevealText>Direction.</RevealText>
                </h2>
                <p className="font-body text-base md:text-lg lg:text-xl text-black/80 leading-relaxed">
                  {project.direction}
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* PHOTOGRAPHY */}
      {hasPhotography && (
        <section className="px-6 md:px-12 pb-8 md:pb-10" data-testid="case-photography">
          <h2 className="font-display text-[clamp(1.75rem,4vw,2.75rem)] uppercase tracking-mega mb-6 md:mb-8">
            <RevealText>The Frames</RevealText>
          </h2>
          <div className="grid grid-cols-3 gap-px -mx-6 sm:mx-0 sm:gap-6 md:gap-8 lg:grid-cols-4">
            {photos.map((photo, i) => (
              <motion.div
                key={photo}
                {...mediaReveal}
                className="w-full aspect-square sm:aspect-[4/5] overflow-hidden bg-black/5"
              >
                <MediaImage
                  publicId={photo}
                  alt={`${project.title} — ${i + 1}`}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
